import {
  Action,
  ActionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Group } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/groups_pb";
import {
  AddActionRequest,
  AddPermissionsRequest,
  AddPermissionsResponse,
  CreateGroupRequest,
  ListActionsByGroupRequest,
  ListAllActionsRequest,
  ListGroupsByActionRequest,
  ListGroupsRequest,
  ListPermissionsRequest,
  ListUserAllowedActionsRequest,
  ListUserAllowedActionsResponse,
  ListUserOktaGroupsRequest,
  Permission,
  RemoveActionRequest,
  RemoveActionResponse,
  RemoveGroupRequest,
  RemoveGroupResponse,
  RemovePermissionsRequest,
  RemovePermissionsResponse,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { supportServiceConnectClient } from "../index";
import { RequestMetadata } from "api/types";

type ListUserAllowedActionsParams = {
  oktaUserId: string;
};

export const listUserAllowedActions = (
  { oktaUserId }: ListUserAllowedActionsParams,
  headers: Record<RequestMetadata, string>
): Promise<ActionType[]> => {
  const req = new ListUserAllowedActionsRequest({
    oktaUserId,
  });

  return supportServiceConnectClient
    .listUserAllowedActions(req, {
      headers,
    })
    .then((resp) => {
      return resp.allowedMethods;
    });
};

export const listUserAllowedActionsWithGroupInfo = (
  { oktaUserId }: ListUserAllowedActionsParams,
  headers: Record<RequestMetadata, string>
): Promise<ListUserAllowedActionsResponse> => {
  const req = new ListUserAllowedActionsRequest({
    oktaUserId,
  });

  return supportServiceConnectClient
    .listUserAllowedActions(req, {
      headers,
    })
    .then((resp) => {
      return resp;
    });
};

type CreateGroupParams = {
  name: string;
};

export const createGroup = (
  { name }: CreateGroupParams,
  headers: Record<RequestMetadata, string>
): Promise<Group | undefined> => {
  const req = new CreateGroupRequest({
    name,
  });

  return supportServiceConnectClient
    .createGroup(req, {
      headers,
    })
    .then((resp) => {
      return resp.group;
    });
};

type RemoveGroupParams = {
  name: string;
};

export const removeGroup = async (
  { name }: RemoveGroupParams,
  headers: Record<RequestMetadata, string>
): Promise<RemoveGroupResponse> => {
  const req = new RemoveGroupRequest({
    name,
  });

  return supportServiceConnectClient.removeGroup(req, {
    headers,
  });
};

export const listGroups = (
  headers: Record<RequestMetadata, string>
): Promise<Group[]> => {
  const req = new ListGroupsRequest();

  return supportServiceConnectClient
    .listGroups(req, {
      headers,
    })
    .then((resp) => {
      return resp.groups;
    });
};

export const listPermissions = async (
  headers: Record<RequestMetadata, string>
): Promise<Permission[]> => {
  const req = new ListPermissionsRequest();
  const resp = await supportServiceConnectClient.listPermissions(req, {
    headers,
  });
  return resp.permissions;
};

type AddPermissionsParams = {
  groupId: string;
  actionsList: Action[];
};

export const addPermissions = async (
  { groupId, actionsList }: AddPermissionsParams,
  headers: Record<RequestMetadata, string>
): Promise<AddPermissionsResponse> => {
  const constructedPermissionsList = [];
  for (const actionListItem of actionsList) {
    const permission = new Permission({
      groupId,
      actionId: actionListItem.id,
    });
    constructedPermissionsList.push(permission);
  }
  const req = new AddPermissionsRequest({
    permissions: constructedPermissionsList,
  });

  return supportServiceConnectClient.addPermissions(req, {
    headers,
  });
};

type RemovePermissionsParams = {
  groupId: string;
  actionsList: Action[];
};

export const removePermissions = async (
  { groupId, actionsList }: RemovePermissionsParams,
  headers: Record<RequestMetadata, string>
): Promise<RemovePermissionsResponse> => {
  const constructedPermissionsList = [];
  for (const actionListItem of actionsList) {
    const permission = new Permission({
      groupId,
      actionId: actionListItem.id,
    });
    constructedPermissionsList.push(permission);
  }
  const req = new RemovePermissionsRequest({
    permissions: constructedPermissionsList,
  });

  return supportServiceConnectClient.removePermissions(req, {
    headers,
  });
};

export const listAllActions = (
  headers: Record<RequestMetadata, string>
): Promise<Action[]> => {
  const req = new ListAllActionsRequest();

  return supportServiceConnectClient
    .listAllActions(req, {
      headers,
    })
    .then((resp) => {
      return resp.actions;
    });
};

type AddActionParams = {
  actionType: ActionType;
};

export const addAction = (
  { actionType }: AddActionParams,
  headers: Record<RequestMetadata, string>
): Promise<Action | undefined> => {
  const req = new AddActionRequest({
    actionType,
  });

  return supportServiceConnectClient
    .addAction(req, {
      headers,
    })
    .then((resp) => resp.action);
};

type RemoveActionParams = {
  actionType: ActionType;
};

export const removeAction = async (
  { actionType }: RemoveActionParams,
  headers: Record<RequestMetadata, string>
): Promise<RemoveActionResponse> => {
  const req = new RemoveActionRequest({
    actionType,
  });

  return supportServiceConnectClient.removeAction(req, {
    headers,
  });
};

type ListActionsByGroupParams = {
  groupId: string;
};

export const listActionsByGroup = (
  { groupId }: ListActionsByGroupParams,
  headers: Record<RequestMetadata, string>
): Promise<Action[]> => {
  const req = new ListActionsByGroupRequest({
    groupId,
  });

  return supportServiceConnectClient
    .listActionsByGroup(req, {
      headers,
    })
    .then((resp) => {
      return resp.actions;
    });
};

type ListGroupsByActionParams = {
  actionType: ActionType;
};

export const listGroupsByAction = (
  { actionType }: ListGroupsByActionParams,
  headers: Record<RequestMetadata, string>
): Promise<Group[]> => {
  const req = new ListGroupsByActionRequest({
    actionType,
  });

  return supportServiceConnectClient
    .listGroupsByAction(req, {
      headers,
    })
    .then((resp) => {
      return resp.groups;
    });
};

export const listUserOktaGroups = (
  headers: Record<RequestMetadata, string>
): Promise<Group[]> => {
  return supportServiceConnectClient
    .listUserOktaGroups(new ListUserOktaGroupsRequest(), {
      headers,
    })
    .then((resp) => {
      return resp.groups;
    });
};
